import { useState } from 'react';
import { Languages, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Los botones de escritura con IA para textareas planos: MEJORAR y TRADUCIR AL INGLÉS.
 *
 * Es el mismo asistente del RichEditor (/api/ai/rewrite) — 2nd opinion, razón de Not Accepted y
 * el MentionTextarea que usan todos los chats y notas. `iconOnly` = versión flotante chiquita
 * para ir DENTRO de la caja de texto sin robar espacio.
 *
 * El de traducir lo pidió Manuel (2026-08-24): media empresa escribe en español y del otro lado
 * (carrier, adjuster, contratista) leen en inglés, así que la nota se copiaba a un traductor y se
 * pegaba de vuelta. Va como botón APARTE del de mejorar y no como una opción escondida: son dos
 * intenciones distintas y traducir no debe cambiar lo que dice el texto.
 *
 * Los dos aparecen recién cuando hay algo escrito: un botón que no puede hacer nada es ruido.
 */
export function ImproveTextButton({ value, onChange, iconOnly = false, style }: {
  value: string; onChange: (v: string) => void; iconOnly?: boolean; style?: React.CSSProperties;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState<'improve' | 'translate_en' | null>(null);

  if (!value.trim()) return null;

  async function pedir(mode: 'improve' | 'translate_en') {
    setBusy(mode);
    try {
      const r = await api.post<{ text: string }>('/api/ai/rewrite', { text: value, mode });
      if (r.text) onChange(r.text);
    } catch { /* si la IA falla, el texto queda como está */ }
    finally { setBusy(null); }
  }

  if (iconOnly) {
    const chico: React.CSSProperties = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
      border: '1px solid var(--line)', borderRadius: 6, background: 'var(--card)', color: 'var(--accent)',
      padding: 0,
    };
    return (
      // Los dos juntos, pegados al borde de la caja; el de traducir a la izquierda del de mejorar.
      <span style={{ position: 'absolute', right: 24, bottom: 8, display: 'inline-flex', gap: 4, ...style }}>
        <button type="button" title={t('re.aiTranslate')} aria-label={t('re.aiTranslate')} disabled={busy != null}
          onClick={() => void pedir('translate_en')} onMouseDown={(e) => e.preventDefault()}
          style={{ ...chico, cursor: busy ? 'default' : 'pointer', opacity: busy === 'translate_en' ? 0.6 : 0.9 }}>
          {busy === 'translate_en' ? '…' : <Languages size={13} strokeWidth={1.75} />}
        </button>
        {/* mousedown+preventDefault: que el textarea no pierda el foco al clickear el botón */}
        <button type="button" title={t('re.aiImprove')} aria-label={t('re.aiImprove')} disabled={busy != null}
          onClick={() => void pedir('improve')} onMouseDown={(e) => e.preventDefault()}
          style={{ ...chico, cursor: busy ? 'default' : 'pointer', opacity: busy === 'improve' ? 0.6 : 0.9 }}>
          {busy === 'improve' ? '…' : <Sparkles size={13} strokeWidth={1.75} />}
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 6, ...style }}>
      <button type="button" className="btn ghost sm" disabled={busy != null} onClick={() => void pedir('improve')}
        style={{ fontSize: 'var(--fs-2xs)' }}>
        {busy === 'improve' ? '…' : <><Sparkles size={13} strokeWidth={1.75} /> {t('re.aiImprove')}</>}
      </button>
      <button type="button" className="btn ghost sm" disabled={busy != null} onClick={() => void pedir('translate_en')}
        style={{ fontSize: 'var(--fs-2xs)' }}>
        {busy === 'translate_en' ? '…' : <><Languages size={13} strokeWidth={1.75} /> {t('re.aiTranslate')}</>}
      </button>
    </span>
  );
}
